import {
  type FocusEventHandler,
  type MouseEvent,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { type DayPickerSingleProps } from "react-day-picker";
import { format, isValid, setHours, setMinutes, setSeconds } from "date-fns";
import {
  Calendar01Icon,
  CalendarIcon,
  ChevronIcon,
  CloseIcon,
} from "vega-icons";

import { DropdownButton } from "@vega-core/components/Buttons/DropdownButton/DropdownButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { Button } from "@vega-core/components/shadcn/button";
import { Calendar } from "@vega-core/components/shadcn/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  DEFAULT_MAX_FUTURE_YEARS,
  MonthYearView,
} from "./components/MonthYearView";
import { type CalendarView } from "./hooks/types";
import { useCalendarCaption } from "./hooks/useCalendarCaption";

type Props = {
  className?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  isOptional?: boolean;
  label?: string;
  labelTag?: ReactNode;
  minDate?: Date;
  onBlur?: FocusEventHandler;
  onChange: (value: Date | undefined) => void;
  onClear?: () => void;
  placeholder?: string;
  placeholderClassName?: string;
  size?: number;
  startAt?: number;
  value?: Date | string;
  withBorder?: boolean;
  isBirthday?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  name: string;
  onlyFuture?: boolean;
  disableFuture?: boolean;
  isSubscriptionQuestion?: boolean;
  hideClearButton?: boolean;
  maxFutureYears?: number;
  constrainInputWidth?: boolean;
  dateFormat?: string;
  maskValue?: boolean;
};

const CALENDAR_CLASS_NAMES = {
  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
  month: "space-y-4",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: "text-sm font-medium",
  caption_dropdowns: "flex justify-center gap-1",
  day: "text-text/primary",
  day_disabled: "text-text/disabled opacity-50",
  day_selected:
    "bg-text/primary text-white hover:bg-text/primary hover:text-white rounded-md",
  day_today: "bg-accent text-accent-foreground",
  day_outside: "text-text/disabled opacity-50",
  cell: "h-9 w-9 text-center p-0 relative focus-within:relative focus-within:z-20",
  button: "w-full h-full",
  row: "flex w-full mt-2",
  table: "w-full border-collapse space-y-1",
  nav: "space-x-1 flex items-center",
  nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
  nav_button_next: "absolute right-1",
  nav_button_previous: "absolute left-1",
  head_cell: "text-text/disabled rounded-md w-9",
  head_row: "flex",
} as const;

export function ThemedDateField({
  disabled,
  error,
  id,
  isOptional,
  label,
  labelTag,
  onChange,
  placeholder = "Pick a date",
  placeholderClassName,
  isBirthday = false,
  value,
  fullWidth = false,
  isLoading = false,
  name,
  onClear,
  isSubscriptionQuestion = false,
  onlyFuture = false,
  disableFuture = false,
  hideClearButton = false,
  maxFutureYears = DEFAULT_MAX_FUTURE_YEARS,
  constrainInputWidth = false,
  dateFormat = "PPP",
  maskValue = false,
}: Props) {
  const shouldMask = maskValue || isBirthday;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(
    typeof value === "string" ? new Date(value) : value,
  );
  const [view, setView] = useState<CalendarView>("DAY");
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date());
  const { renderCaption } = useCalendarCaption();

  const handleDateChange = (selectedDate: Date) => {
    const normalizedDate = setSeconds(
      setMinutes(setHours(selectedDate, 12), 0),
      0,
    );
    setDate(normalizedDate);
    onChange(normalizedDate);
    setOpen(false);
  };

  const handleMonthYearSelect = (date: Date) => {
    setDisplayMonth(date);

    if (view === "MONTH" || view === "YEAR") {
      setView("DAY");
    }
  };

  const handleMonthYearNavigate = useCallback((date: Date) => {
    if (!isValid(date)) return;
    setDisplayMonth(date);
  }, []);

  const handleMonthChange = useCallback((date: Date) => {
    if (!isValid(date)) return;
    setDisplayMonth(date);
  }, []);

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setOpen(false);
      setTimeout(() => {
        setView("DAY");
      }, 100);
    } else {
      setOpen(true);
    }
  }, []);

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setDate(undefined);
      setOpen(false);
      onChange(undefined);

      if (onClear) onClear();
    },
    [onChange, onClear],
  );

  const today = new Date();
  const dateConstraints: DayPickerSingleProps = {
    mode: "single",
    captionLayout: "dropdown",
    ...(isBirthday
      ? {
          toYear: today.getFullYear(),
          fromYear: 1900,
          disabled: {
            after: today,
          },
        }
      : {}),
    ...(onlyFuture
      ? {
          disabled: {
            before: today,
          },
          defaultMonth: date || today,
        }
      : {}),
    ...(disableFuture
      ? {
          toYear: today.getFullYear(),
          disabled: {
            after: today,
          },
          defaultMonth: date || today,
        }
      : {}),
  };

  useEffect(() => {
    setDate(typeof value === "string" ? new Date(value) : value);
  }, [value]);

  return (
    <div className={cnTwMerge("group", fullWidth && "w-full")} id={id}>
      <div className={cnTwMerge(fullWidth && "w-full")}>
        <FormLabel isOptional={isOptional} label={label} tag={labelTag} />
      </div>
      <div
        className={cnTwMerge("w-full", {
          "[&>button]:w-full": fullWidth,
          "max-w-[50%]": constrainInputWidth,
        })}
      >
        <Popover onOpenChange={handlePopoverOpenChange} open={open}>
          <PopoverTrigger asChild>
            <div>
              <DropdownButton
                className={cnTwMerge("w-[200px]", {
                  "border-border/negative": isTruthy(error),
                  "w-full": fullWidth,
                  "[&>span>span]:w-full": isSubscriptionQuestion,
                })}
                dataTestId={`${name}-date-trigger`}
                disabled={disabled}
                icon={
                  <div className="flex items-center gap-2">
                    <ChevronIcon
                      className={cnTwMerge(
                        "h-4 w-4 shrink-0 text-text/secondary defaultTransition",
                        !disabled && "group-hover:text-text/interactive",
                        open && "rotate-180",
                      )}
                    />
                    {date && !hideClearButton ? (
                      <Button
                        aria-label="Clear date"
                        className={cnTwMerge(
                          "h-4 w-4 rounded-full text-text/secondary bg-background/tertiary transition hover:bg-background/primary cursor-pointer",
                        )}
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClear(e);
                        }}
                        size="icon"
                        type="button"
                        variant="link"
                      >
                        <CloseIcon className="h-3 w-3" />
                      </Button>
                    ) : null}
                  </div>
                }
                isLoading={isLoading}
                onClick={() => {
                  setOpen(!open);
                }}
                open={open}
                type="button"
              >
                <div
                  className="flex items-center w-full gap-3"
                  {...(shouldMask && { "data-ph-mask": "" })}
                >
                  {isSubscriptionQuestion ? (
                    <CalendarIcon
                      aria-hidden="true"
                      className="text-text/secondary"
                    />
                  ) : (
                    <Calendar01Icon aria-hidden="true" className="h-4 w-4" />
                  )}

                  {date ? (
                    format(date, dateFormat)
                  ) : (
                    <span className={placeholderClassName}>{placeholder}</span>
                  )}
                </div>
              </DropdownButton>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="flex w-auto flex-col space-y-2 p-0 z-[60]"
            data-testid="themed-date-popover"
          >
            <div className="rounded-md border">
              {view === "DAY" ? (
                <Calendar
                  captionLayout="dropdown"
                  className="rounded-md border"
                  classNames={CALENDAR_CLASS_NAMES}
                  components={{
                    Caption: (props) =>
                      renderCaption(
                        {
                          ...props,
                          onClick: handleMonthChange,
                          numberOfMonths: 1,
                        },
                        view,
                        setView,
                      ),
                  }}
                  fixedWeeks
                  month={displayMonth}
                  name={name}
                  numberOfMonths={1}
                  onMonthChange={handleMonthChange}
                  onSelect={(
                    selectedDate: SetStateAction<Date | undefined>,
                  ) => {
                    if (selectedDate) {
                      handleDateChange(selectedDate as Date);
                    }
                  }}
                  selected={date}
                  showOutsideDays={false}
                  {...dateConstraints}
                />
              ) : (
                <MonthYearView
                  displayMonth={displayMonth}
                  isBirthday={isBirthday}
                  maxFutureYears={maxFutureYears}
                  name={name}
                  onNavigate={handleMonthYearNavigate}
                  onSelect={handleMonthYearSelect}
                  view={view}
                />
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isTruthy(error) && (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      )}
    </div>
  );
}
